import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  cloneElement,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { noop, insert, detach } from 'svelte/internal';

const eventRegExp = /^on([A-Z]{1,}.*)$/;
const watchRegExp = /^watch([A-Z]{1,}.*)$/;
const slotRegExp = /^slot([A-Z]{1,}.*)$/;
const defaultCreateSlotContainer = () => document.createElement('div');

const Svelte = ({
  component: Component,
  container: baseContainer,
  createSlotContainer = defaultCreateSlotContainer,
  ...rest
}) => {
  const containerRef = useRef(null);
  const mountedRef = useRef(false);
  const instanceRef = useRef();
  const watchHandlersRef = useRef(new Map());
  const slotContainersRef = useRef(new Map());
  const createSlotContainerRef = useRef(createSlotContainer);
  const [slotsVisible, setSlotsVisible] = useState([]);

  useEffect(() => {
    createSlotContainerRef.current = createSlotContainer;
  }, [createSlotContainer]);

  const container = useMemo(() => {
    if (!baseContainer) {
      return React.createElement('div', { ref: containerRef });
    }
    return cloneElement(baseContainer, {
      ...baseContainer.props,
      ref: (node) => {
        containerRef.current = node;
        const { ref } = baseContainer;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
    });
  }, [baseContainer]);

  const [eventHandlers, slots, props] = useMemo(() => {
    const eventHandlers = {};
    const watchHandlers = new Map();
    const slots = [];
    const props = {
      $$scope: {},
      $$slots: {},
    };
    Object.entries(rest).forEach(([name, prop]) => {
      if (typeof prop === 'function') {
        const eventHandlerMatch = name.match(eventRegExp);
        if (eventHandlerMatch) {
          eventHandlers[
            `${eventHandlerMatch[1][0].toLowerCase()}${eventHandlerMatch[1].slice(1)}`
          ] = prop;
          return;
        }
        const watchHandlerMatch = name.match(watchRegExp);
        if (watchHandlerMatch) {
          const name = `${watchHandlerMatch[1][0].toLowerCase()}${watchHandlerMatch[1].slice(1)}`;
          watchHandlers.set(name, {
            handler: prop,
            prevValue: watchHandlersRef.current.get(name)?.prevValue,
          });
          return;
        }
      }
      const slotMatch = name.match(slotRegExp);
      if (slotMatch || name === 'children') {
        const slotName = slotMatch
          ? `${slotMatch[1][0].toLowerCase()}${slotMatch[1].slice(1)}`
          : 'default';
        if (!slotContainersRef.current.has(slotName)) {
          slotContainersRef.current.set(
            slotName,
            createSlotContainerRef.current(slotName),
          );
        }
        const container = slotContainersRef.current.get(slotName);
        slots.push({
          name: slotName,
          node: prop,
          container,
        });
        props.$$slots[slotName] = [
          () => ({
            c: noop,
            l: noop,
            m: (target, anchor) => {
              setSlotsVisible((slots) => [...slots, slotName]);
              insert(target, container, anchor);
            },
            d: (destroy) => {
              if (destroy) {
                setSlotsVisible((slots) => slots.filter((s) => s !== slotName));
                detach(container);
              }
            },
          }),
        ];
        return;
      }
      // Only assign props that are not undefined to prevent toString errors in Svelte components
      if (prop !== undefined) {
        props[name] = prop;
      }
    });

    watchHandlersRef.current = watchHandlers;

    Object.entries(props).forEach(([name, value]) => {
      if (watchHandlers.has(name)) {
        watchHandlers.get(name).prevValue = value;
      }
    });

    return [eventHandlers, slots, props];
  }, [rest]);

  useLayoutEffect(() => {
    const instance = new Component({
      target: containerRef.current,
      props,
    });
    instanceRef.current = instance;
    instance.$$.after_update.push(() => {
      const watchHandlers = watchHandlersRef.current;
      watchHandlers.forEach(({ handler, prevValue }, name) => {
        const index = instance.$$.props[name];
        const value = instance.$$.ctx[index];
        if (!Object.is(value, prevValue)) {
          watchHandlers.get(name).prevValue = value;
          handler(value);
        }
      });
    });
    return () => {
      instance.$destroy();
    };
  }, [Component]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    instanceRef.current.$set(props);
  }, [props]);

  useEffect(() => {
    const instance = instanceRef.current;
    const teardown = Object.entries(eventHandlers).map(([name, handler]) =>
      instance.$on(name, handler),
    );
    return () => {
      teardown.forEach((h) => h());
    };
  }, [eventHandlers]);

  return React.createElement(
    React.Fragment,
    null,
    container,
    slots.map(
      ({ name, node, container }) =>
        slotsVisible.includes(name) && createPortal(node, container),
    ),
  );
};

export { Svelte };
